import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  Alert,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useBankingAccounts } from "Api/Queries/Accounts/useBankingAccounts";
import {
  type BankEnvironment,
  BankAccountStatus,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { BottomDrawer } from "Components/Chat/BottomDrawer";
import { AmountField } from "Components/Chat/AmountField";
import { BankAccountIDSelect } from "Components/Chat/BankAccountIDSelect";
import CloseIcon from "@mui/icons-material/Close";
import { formatCurrency } from "Utils/CurrencyUtils";

const VALIDATION_RESOURCES = Resources.Chat.PaymentRequest.Drawer.Validation;

const parseAmount = (raw: string): number => Number(raw.replace(",", "."));

const getSchema = (t: (key: string) => string) =>
  z.object({
    bankAccountID: z.number({
      error: t(VALIDATION_RESOURCES.SelectBankAccount),
    }),
    // The amount is held as the raw typed string (so the input can preserve an in-progress
    // trailing separator) and validated by parsing; it is converted to a number at submit.
    amount: z
      .string()
      .refine((v) => v.trim().length > 0, t(VALIDATION_RESOURCES.EnterAmount))
      .refine((v) => {
        const parsed = parseAmount(v);
        return !Number.isNaN(parsed) && parsed > 0;
      }, t(VALIDATION_RESOURCES.AmountMustBePositive)),
    note: z.string().optional(),
  });

export type PaymentDrawerFormData = z.infer<ReturnType<typeof getSchema>>;

// The amount reaches the caller as a parsed number, decoupled from the string the form holds.
// The account's environment rides along so a PISP-first bank (mBank) can be authorized without
// a second lookup of the account list.
export type PaymentSubmitData = {
  amount: number;
  bankAccountID: number;
  bankAccountEnvironment: BankEnvironment;
  note?: string;
};

/**
 * A payment whose amount is NOT the user's to choose (paying a multi-payer request): the form
 * shows it read-only in place of the amount input and submits it as typed by the author.
 */
export type FixedAmount = {
  amount: number;
  currencyCode: string;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  bankAccountLabel: string;
  amountLabel: string;
  submitLabel: string;
  noteLabel: string;
  notePlaceholder: string;
  isPending: boolean;
  error: unknown;
  errorContent?: ReactNode;
  fixedAmount?: FixedAmount;
  onSubmit: (data: PaymentSubmitData) => void;
};

export const PaymentDrawerForm = ({
  isOpen,
  onClose,
  title,
  bankAccountLabel,
  amountLabel,
  submitLabel,
  noteLabel,
  notePlaceholder,
  isPending,
  error,
  errorContent,
  fixedAmount,
  onSubmit,
}: Props) => {
  const { t } = useResource();
  const { data: bankAccounts, isLoading: isLoadingAccounts } =
    useBankingAccounts();

  const activeAccounts =
    bankAccounts?.items?.filter(
      ({ status }) =>
        status === BankAccountStatus.Active ||
        status === BankAccountStatus.Restricted,
    ) ?? [];

  const { control, handleSubmit, setValue, reset, formState } =
    useForm<PaymentDrawerFormData>({
      resolver: zodResolver(getSchema(t)),
      defaultValues: {
        amount: fixedAmount ? String(fixedAmount.amount) : "",
        note: "",
      },
    });

  const defaultAccountID =
    activeAccounts.find((a) => a.isPrimary)?.bankAccountID ??
    activeAccounts[0]?.bankAccountID;

  // Validate on preselect so isValid reflects the filled account: with a fixed amount nothing
  // else is typed, and without it the submit would stay disabled until the user edits a field.
  useEffect(() => {
    if (isOpen && defaultAccountID !== undefined) {
      setValue("bankAccountID", defaultAccountID, { shouldValidate: true });
    }
  }, [isOpen, defaultAccountID, setValue]);

  const amountInputRef = useRef<HTMLInputElement | null>(null);
  const focusAmountField = useCallback(() => {
    amountInputRef.current?.focus();
  }, []);

  const [isNoteVisible, setIsNoteVisible] = useState(false);

  // Reset only after the close animation finishes (any close path — dismiss or success — animates
  // out and fires this). Resetting while the drawer is still visible would flash the fields to
  // empty; a failed submit keeps the drawer open, so the entered values survive for a retry.
  const handleClosed = () => {
    reset();
    setIsNoteVisible(false);
  };

  const handleFormSubmit = (data: PaymentDrawerFormData) => {
    const account = activeAccounts.find(
      ({ bankAccountID }) => bankAccountID === data.bankAccountID,
    );
    if (!account) {
      return;
    }
    onSubmit({
      amount: parseAmount(data.amount),
      bankAccountID: data.bankAccountID,
      bankAccountEnvironment: account.environment,
      note: data.note,
    });
  };

  return (
    <BottomDrawer
      isOpen={isOpen}
      onClose={onClose}
      onTransitionEnd={focusAmountField}
      onClosed={handleClosed}
    >
      <Stack gap={2}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6" fontWeight={600}>
            {title}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
        {isLoadingAccounts ? (
          <CircularProgress />
        ) : (
          <form onSubmit={handleSubmit(handleFormSubmit)}>
            <Stack gap={3}>
              {fixedAmount ? (
                <Stack alignItems="center" gap={0.5}>
                  <Typography fontWeight={400} color="text.secondary">
                    {amountLabel}
                  </Typography>
                  <Typography fontSize="2rem" fontWeight={700}>
                    {formatCurrency(fixedAmount)}
                  </Typography>
                </Stack>
              ) : (
                <AmountField
                  control={control}
                  name="amount"
                  label={amountLabel}
                  inputRef={amountInputRef}
                />
              )}
              <BankAccountIDSelect
                control={control}
                label={bankAccountLabel}
                accounts={activeAccounts}
              />
              {isNoteVisible ? (
                <div>
                  <Typography fontWeight={400} mb={1}>
                    {noteLabel}
                  </Typography>
                  <TextField
                    {...control.register("note")}
                    placeholder={notePlaceholder}
                    multiline
                    minRows={2}
                    maxRows={4}
                    fullWidth
                  />
                </div>
              ) : (
                <Button
                  variant="text"
                  onClick={() => setIsNoteVisible(true)}
                  sx={{
                    textTransform: "none",
                    fontSize: "0.8rem",
                    alignSelf: "flex-end",
                    p: 0,
                  }}
                >
                  + {noteLabel}
                </Button>
              )}
              {!!error && <Alert severity="error">{errorContent}</Alert>}
              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={isPending || !formState.isValid}
                startIcon={
                  isPending ? <CircularProgress size={20} /> : undefined
                }
                sx={{
                  "&.Mui-disabled": {
                    color: "white",
                  },
                }}
              >
                {submitLabel}
              </Button>
            </Stack>
          </form>
        )}
      </Stack>
    </BottomDrawer>
  );
};
